import { AnswerFromMemory, JobTrade } from "@gigsync/protocol";
import { EVERY_DAY, isMonthly } from "../job/when.js";
import { QUESTIONS } from "../job/worker.js";
import { priceOpen } from "./driver-ask.js";
import { arrangedWork, needsArrangement } from "./need-arrangement.js";
/**
 * The need interview's shape and how it moves: a household (or a worker hiring a helper) says what they need, in one
 * sentence or several turns, and the interview asks only for what is still missing, then reads it back before any
 * search. Reading the answers is `need-hear.ts`; the questions are `need-prompt.ts`.
 */
/** A day said as a word; resolved to the phone's calendar day only when the request is sent. */
export const DayWord = { today: "today", tomorrow: "tomorrow" };
/** Where the interview is: a field being asked, the readback, the search, or what it found. */
export const NeedSlot = {
    trade: "trade",
    /** The scope read from the owner's words, as chips to confirm. */
    scope: "scope",
    detail: "detail",
    /** How long the work is for, for maid and driver work (ADR 0046). */
    arrangement: "arrangement",
    /** A price driver that moves the range most (ADR 0041), asked before the estimate. */
    driver: "driver",
    area: "area",
    when: "when",
    budget: "budget",
    confirm: "confirm",
    /** The owner said yes: the app searches the board now. */
    done: "done",
    /** The search found workers: the app shows them to pick and ask. */
    found: "found",
    /** The search found nobody: look further, or change something. */
    nobody: "nobody",
    /** A need outside the trades: GigSync has no prices for it yet and nobody offers it on GigSync. */
    outside: "outside",
};
export const NEED_FIELDS = [
    NeedSlot.trade,
    NeedSlot.detail,
    NeedSlot.arrangement,
    NeedSlot.area,
    NeedSlot.when,
    NeedSlot.budget,
];
/** How far the board is searched, nearest first; the first is where every search starts. */
export const SEARCH_RADII_KM = [5, 10, 25];
/** The trade's first question, which the request answers up front so the worker's agent need not ask it. */
export const firstQuestion = (trade) => trade ? QUESTIONS[trade][0] : undefined;
/** The kind of work a draft is for: its trade, `other` for a listed skill (ADR 0047), or not known yet. */
export const workOf = (d) => d.trade ?? (d.skill ? JobTrade.enum.other : undefined);
/**
 * The next field still missing, in the order asked; confirm once the need is whole. A household's need is not whole
 * without one of its saved addresses (ADR 0045): an area alone narrows the search, but the worker must be sent to a
 * real door, never to whatever address an older screen once took.
 */
export function firstMissing(d, needsAddress = false) {
    if (!d.trade && !d.skill && d.jobType)
        return priceOpen(d) ? NeedSlot.driver : NeedSlot.outside;
    if (!d.trade && !d.skill)
        return NeedSlot.trade;
    if (d.scope && !d.scope.confirmed)
        return NeedSlot.scope;
    if (firstQuestion(workOf(d)) && d.detail === undefined)
        return NeedSlot.detail;
    if (needsArrangement(d))
        return NeedSlot.arrangement;
    if (d.area === undefined || (needsAddress && d.address === undefined))
        return NeedSlot.area;
    if (priceOpen(d))
        return NeedSlot.driver;
    // The day, then the hour in it (need-time.ts); a time said with the day answers both.
    if (d.day === undefined || d.time === undefined)
        return NeedSlot.when;
    if (d.budget_paise === undefined)
        return NeedSlot.budget;
    return NeedSlot.confirm;
}
/**
 * The draft with a trade set: the detail follows the trade's own first question (from what the owner already said,
 * or asked next), and monthly work is every day unless the owner said otherwise.
 */
export function withTrade(d, trade) {
    if (d.trade === trade)
        return d;
    const first = firstQuestion(trade);
    const day = d.day ?? (isMonthly(trade) ? EVERY_DAY : undefined);
    return { ...d, trade, skill: undefined, detail: first ? d.seen[first] : undefined, ...(day ? { day } : {}) };
}
/** A new need; addressed `to` one worker, it is for their trade from the start when their card has only one. */
export function startNeed(config, to) {
    const work = [...(to?.trades ?? []), ...(to?.skills ?? [])];
    const [only] = work;
    const empty = config.modelQuestions ? { seen: {}, modelAsk: { said: [], covered: [] } } : { seen: {} };
    const one = work.length === 1 ? only : undefined;
    const draft = one === undefined ? empty : typeof one === "string" ? withTrade(empty, one) : { ...empty, skill: one };
    return {
        config,
        draft,
        ...(to ? { to } : {}),
        slot: NeedSlot.trade,
        notes: [],
        changing: false,
        fresh: true,
        radius_km: SEARCH_RADII_KM[0],
        ownAnswer: [],
        filled: [],
    };
}
/** A trade the need can be for: any, or one on the card of the worker it is addressed to. */
export const tradeFor = (s, trade) => trade !== undefined && (!s.to || s.to.trades.includes(trade));
/** The same question again, after a note saying why. */
export const retryNeed = (s, note) => ({ ...s, notes: [note], fresh: false });
/** The remembered answer for a field (the one asked now by default), unless the owner chose to answer it. */
export function recalled(s, field = s.slot) {
    const memory = s.config.remembered;
    if (!memory || s.ownAnswer.includes(field))
        return undefined;
    const question = field === NeedSlot.detail ? firstQuestion(workOf(s.draft)) : undefined;
    const fact = question ? memory.facts[question] : undefined;
    if (question && fact)
        return { known: fact, question };
    const area = field === NeedSlot.area ? memory.area : undefined;
    return area && s.config.areas.some((a) => a.key === area.value) ? { known: area, area: area.value } : undefined;
}
/** "Ask me first": the remembered answer for the question now asked, offered to use or change. */
export const offered = (s) => s.config.remembered?.mode === AnswerFromMemory.enum.ask ? recalled(s) : undefined;
const withRecalled = (d, field, value) => field === NeedSlot.area ? { ...d, area: value } : { ...d, detail: value };
/** The household's one saved address in the area the need is in, taken without asking; else the draft as it is. */
function withOnlyAddress(config, d) {
    if (!config.addresses || d.area === undefined || d.address !== undefined)
        return d;
    const inArea = config.addresses.filter((a) => a.area === d.area);
    const only = inArea.length === 1 ? inArea[0] : undefined;
    return only ? { ...d, area: only.area, areaName: only.areaName, address: only.id } : d;
}
/** Where a need goes next: the household's address settled if it can be, then whatever is still missing. */
function nextSlot(config, d) {
    const draft = withOnlyAddress(config, d);
    return { draft, slot: firstMissing(draft, config.addresses !== undefined) };
}
/** "Answer for me": each fresh remembered answer fills its question as it comes up (the prompt says so). */
function autoFilled(s) {
    if (s.config.remembered?.mode !== AnswerFromMemory.enum.auto)
        return s;
    let next = s;
    for (let r = recalled(next); r?.known.fresh; r = recalled(next)) {
        const { draft, slot } = nextSlot(next.config, withRecalled(next.draft, next.slot, r.known.value));
        next = { ...next, draft, slot, filled: [...next.filled, next.slot] };
    }
    return next;
}
/** An answer taken: on to whatever is still missing (filling what memory may), or the readback. */
export const moveOn = (s, draft, notes = []) => autoFilled({ ...s, ...nextSlot(s.config, draft), notes, changing: false, fresh: false, filled: [] });
/** "Use it": the remembered answer offered for the question now asked; anything else changes nothing. */
export function takeRecalled(s) {
    const r = offered(s);
    return r ? moveOn(s, withRecalled(s.draft, s.slot, r.known.value)) : s;
}
/** "Change" on an offer: the owner answers the question themselves, asked as usual. */
export const answerMyself = (s) => offered(s) ? { ...s, ownAnswer: [...s.ownAnswer, s.slot], notes: [], fresh: false } : s;
/** Jumps to one field from the readback (or a Change chip); once it is answered the readback comes again. */
export function editNeed(s, field) {
    const usable = (field !== NeedSlot.detail || firstQuestion(workOf(s.draft)) !== undefined) &&
        (field !== NeedSlot.arrangement || arrangedWork(s.draft));
    const ownAnswer = [...s.ownAnswer, field];
    const slot = usable ? field : NeedSlot.confirm;
    return { ...s, slot, notes: [], changing: !usable, fresh: false, ownAnswer, filled: [] };
}
/** What the board search found: workers to pick from, or nobody, with ways to look further. */
export const searchedNeed = (s, count) => ({
    ...s,
    slot: count > 0 ? NeedSlot.found : NeedSlot.nobody,
    notes: [],
});
/** The search did not finish (no internet): say why and stand at the readback, so a yes tries again. */
export const searchFailed = (s, note) => ({
    ...s,
    slot: NeedSlot.confirm,
    notes: [note],
    changing: false,
});
/** The same interview under the owner's new language: questions from here on, and the hints, follow it. */
export const needWithConfig = (s, config) => ({ ...s, config });
//# sourceMappingURL=need.js.map